import React, { useState } from "react";
import { Img, useRemotionEnvironment } from "remotion";

/**
 * 🛡 씬/콘텐츠 이미지 — 크래시 내성 래퍼(회장님 2026-07-22 "썸네일·미리보기 통째 먹통").
 *
 * 맨 Remotion `<Img>` 는 이미지 로드 실패 시 maxRetries(기본 2) 후 `cancelRender('Error loading image…')` 로
 * **컴포지션 전체를 크래시**시킨다(node_modules/remotion Img.js 실측). 갤러리 썸네일(`<Thumbnail>`)·미리보기
 * (`<Player>`)에선 이미지 하나만 삐끗해도(생성 직후 전파 지연·순간 네트워크 실패 등 TOCTOU) 그 컷이 아니라
 * **컴포지션 전체가 검정으로 죽고 재마운트 전까지 고착**된다.
 *
 * 그래서 프리뷰/썸네일(비 렌더)에선 순수 `<img>` 로 그린다 — 로드 실패해도 브라우저가 깨진 아이콘만 표시하고
 * React/Remotion throw·delayRender 게이트가 없어 컴포지션이 죽지 않는다(그 컷만 빔). 실제 MP4 렌더
 * (renderMediaOnWeb=`isClientSideRendering` / 서버 렌더=`isRendering`)에선 종전대로 `<Img>` 로 프레임 로드
 * 게이팅을 유지한다(빈 프레임 방지). ★게이트는 각 템플릿의 AudioTag(`isRendering||isClientSideRendering`) 와 동일★.
 */
export const SceneImg: React.FC<React.ComponentPropsWithoutRef<typeof Img>> = (props) => {
  const env = useRemotionEnvironment();
  if (env.isRendering || env.isClientSideRendering) return <Img {...props} />;
  return <PreviewImg {...props} />;
};

/** 프리뷰/썸네일: Remotion 전용 prop(순수 <img> 속성 아님)은 제거해 DOM 경고 방지. onError/crossOrigin/src/style 등은 통과. */
const PreviewImg: React.FC<React.ComponentPropsWithoutRef<typeof Img>> = (props) => {
  const {
    pauseWhenLoading: _pauseWhenLoading,
    delayRenderRetries: _delayRenderRetries,
    delayRenderTimeoutInMilliseconds: _delayRenderTimeoutInMilliseconds,
    onImageFrame: _onImageFrame,
    maxRetries: _maxRetries,
    ...imgProps
  } = props;
  return <img {...(imgProps as React.ImgHTMLAttributes<HTMLImageElement>)} alt={imgProps.alt ?? ""} />;
};

/**
 * 🖼 장식 이미지(머리줄 채널명·이모지 자리 등) — SceneImg 와 같은 게이트 + **못 불러오면 그 그림만 빼고 계속**.
 *
 * 종전엔 순수 `<img>`(crossOrigin 없음)로 그려, 런타임 URL(supabase channel-art 등)을 올린 작업물에서
 * web-renderer 캔버스가 오염(tainted)됐다 → 영상 받기 `VideoFrame … tainted sources` · 커버 지정
 * `convertToBlob … tainted OffscreenCanvas` 로 둘 다 실패(10-19 손님 제보).
 *
 * - 렌더(`isClientSideRendering`/`isRendering`) = Remotion `<Img>` — 브라우저 렌더면 crossOrigin='anonymous'
 *   자동(getCrossOriginValue) + 로드 게이팅(빈 프레임 방지).
 * - ★장식 하나 때문에 영상 전체를 죽이지 않는다★ — `<Img>` 는 onError 를 주면 재시도 뒤 cancelRender 대신
 *   onError 를 부른다. 그때 null 로 언마운트하면 정리 함수가 delayRender 를 풀어 렌더가 그 그림 없이 이어진다
 *   (CORS 머리 없는 호스트·깨진 주소 = 그 자리만 빔).
 * - 프리뷰/썸네일 = 종전 순수 `<img>`(crossOrigin 없음) — ACAO 없는 호스트도 미리보기는 그대로 보인다.
 */
export const DecorImg: React.FC<React.ComponentPropsWithoutRef<typeof Img>> = (props) => {
  const env = useRemotionEnvironment();
  const [failedSrc, setFailedSrc] = useState<string | null>(null);
  if (!(env.isRendering || env.isClientSideRendering)) return <PreviewImg {...props} />;
  if (failedSrc !== null && failedSrc === props.src) return null;
  const { onError, ...rest } = props;
  return (
    <Img
      {...rest}
      onError={(e) => {
        // eslint-disable-next-line no-console
        console.warn(`[DecorImg] 장식 이미지를 불러오지 못해 빼고 렌더합니다: ${props.src}`);
        setFailedSrc(props.src ?? "");
        onError?.(e);
      }}
    />
  );
};
